import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../state/app.state';
import { FetchProjectsListRequest } from '../../../core/projects.actions';
import { projectsList } from '../../../core/projects.selectors';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { ProjectsService } from '../../../core/projects.service';
import { isLoading } from '../../../../../shared/core/shared.selectors';
import { CommonService } from '../../../../../shared/services/common.service';
import { AuthService } from '../../../../../shared/services';
import { ApprovalQueueService } from '../../../../approvals/core/approval-queue.service';
import { ApprovalQueueMeta } from '../../../../approvals/core/approval-queue.models';
import { neverApproves, showsReviewStrip } from '../../../../approvals/core/approval-queue.math';

@Component({
    selector: 'app-projects-page',
    templateUrl: './projects-page.component.html',
    styleUrls: ['./projects-page.component.scss'],
    standalone: false
})
export class ProjectsPageComponent implements OnInit, OnDestroy {
  initialized = false;
  projects: any[] = [];
  filteredProjects: any[] = [];
  paginatedFilteredProjects: any[];
  // USED FOR API CALL
  pagination = {
    pageIndex: 0,
    length: 0,
    pageSize: 1000,
  };
  itemsPerPage = 5;
  currentPage = 1;
  @ViewChild('searchInputEl') searchInputEl: ElementRef<HTMLInputElement>;
  isLoading = false;
  frequency = 'Recently updated';
  searchQuery = '';
  isSearchActive = false;
  expiringWarrantyCount = 0;
  // Entry point to the review queue. Populated only for users who can approve
  // somewhere (meta.approver_project_count > 0) — see the template guard.
  reviewQueue: ApprovalQueueMeta | null = null;

  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private router: Router,
    private route: ActivatedRoute,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private approvalQueueService: ApprovalQueueService,
  ) {
  }

  ngOnInit(): void {
    this.store.select(isLoading)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(loading => {
        this.isLoading = !!loading?.length;
        this.cdr.detectChanges();
      });

    this.store.dispatch(
      FetchProjectsListRequest({
        payload: {
          qp: {
            start: this.pagination.pageIndex * this.pagination.pageSize,
            total: this.pagination.pageSize,
            all: 'all',
            include: [
              'sites',
              'cover_photo',
              'status',
              'summary',
              'online',
              'material_types',
              'gauge_numbers',
              'material_sources',
              'warranty_summary',
            ],
          },
        },
      }),
    );

    this.store
      .select(projectsList)
      .pipe(
        takeUntil(this.onDestroy))
      .subscribe(data => {
        this.projects = data;
        this.expiringWarrantyCount = (this.projects || [])
          .filter(p => p?.warranty_summary?.is_expiring).length;
        this.filteredProjects = this.projects;
        this.filterProjects();
        this.cdr.detectChanges();
      });

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.fs === undefined) {
          if (qp.frequency === 'warranty') {
            this.frequency = 'Expiring warranty';
            this.filterProjects();
          } else if (qp.frequency && qp.frequency === '0' || qp.frequency === '-1') {
            this.frequency = qp.frequency !== '-1' ? 'Recently updated' : 'All';
            this.filterProjects();
          } else {
            this.router.navigate([], {
              queryParams: {
                frequency: this.frequency !== 'All' ? 0 : -1,
              },
              queryParamsHandling: 'merge',
            });
          }
        }
      });

    // Contractors and reviewers never approve; don't probe for them (spec §7).
    if (!neverApproves(this.authService.getCurrentUser()?.roles)) {
      this.approvalQueueService.queueSummary().then(meta => {
        this.reviewQueue = meta;
      });
    }
  }

  onSearchClick(event: Event) {
    event?.stopPropagation();
    this.isSearchActive = true;
    setTimeout(() => this.searchInputEl?.nativeElement?.focus(), 0);
  }

  clearSearch() {
    this.searchQuery = '';
    this.isSearchActive = false;
    this.currentPage = 1;
    this.commonService.resetPaginator.next(true);
    this.filterProjects();
  }

  onSearch(query: string) {
    this.searchQuery = query?.trim()?.toLowerCase() || '';
    this.currentPage = 1;
    this.commonService.resetPaginator.next(true);
    this.filterProjects();
  }

  filterProjects() {
    this.filteredProjects = this.projects.filter((project) => {
      let matchesFrequency: boolean;
      if (this.frequency === 'Expiring warranty') {
        matchesFrequency = !!project?.warranty_summary?.is_expiring;
      } else if (this.frequency === 'All') {
        matchesFrequency = true;
      } else {
        // "Recently updated": last 30 days of activity — plus any project with
        // an expiring warranty, so those never drop out of the default list
        // (the banner above the list already counts them; a user should not
        // have to switch filters to find them).
        matchesFrequency = moment(project?.summary?.last_updated_at).isAfter(moment().subtract(30, 'days'))
          || !!project?.warranty_summary?.is_expiring;
      }

      const matchesSearch = !this.searchQuery
        || project?.name?.toLowerCase()?.includes(this.searchQuery)
        || project?.number?.toLowerCase()?.includes(this.searchQuery);

      // Search across accessible projects; restore the selected filter when cleared.
      return this.searchQuery ? matchesSearch : matchesFrequency;
    })
      .sort((a, b) => {
        const maxDateA = moment(a?.summary?.last_updated_at);
        const maxDateB = moment(b?.summary?.last_updated_at);
        // @ts-ignore
        return maxDateB - maxDateA;
      });

    this.updateFilteredProjectList();
  }

  setFrequency(value) {
    this.currentPage = 1;
    this.commonService.resetPaginator.next(true);
    this.frequency = value;

    let frequencyParam: string | number;
    if (value === 'Expiring warranty') {
      frequencyParam = 'warranty';
    } else if (value === 'All') {
      frequencyParam = -1;
    } else {
      frequencyParam = 0;
    }

    this.router.navigate([], {
      queryParams: { frequency: frequencyParam },
    });

    this.filterProjects();
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.updateFilteredProjectList();
  }

  updateFilteredProjectList(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredProjects.length);
    this.paginatedFilteredProjects = this.filteredProjects?.slice(startIndex, endIndex);
    this.projectsService.filteredProjectsForMap.next(this.projects);
  }

  get showReviewQueue(): boolean {
    return showsReviewStrip(this.reviewQueue);
  }

  openReviewQueue(): void {
    this.router.navigate(['/approvals']);
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
